'use client';
import { Badge, Button, Dropdown, Modal, Separator, Tooltip, useToast } from '@ethsign/ui';

import React, { forwardRef, Fragment, ReactNode, useEffect, useImperativeHandle, useRef, useState } from 'react';

import { useEffectOnce } from 'react-use';

import {
  CardanoIcon,
  CoinbaseIcon,
  GoogleIcon,
  LeatherIcon,
  Loading,
  LogoIcon,
  LogoIconLight,
  MetamaskIcon,
  NetworkTag,
  OkxIcon,
  PetraIcon,
  PhantomIcon,
  StarkNetIcon,
  TonIcon,
  TwitterIcon,
  UnisatIcon,
  WalletConnectIcon
} from '@/components';
import { ErrorCode } from '@/constants';
import { useSearchParams } from '@/hooks';
import { login } from '@/services';
import { getUserWalletType, isServer, reportGAEvent, WalletType } from '@/utils';
import { WalletFactory } from '@/utils/wallet/WalletFactory';
import { ADAWallet } from '@/utils/wallet/providers/ada';
import { ParticleWallet } from '@/utils/wallet/providers/particle';
import { ExtendedWalletType } from '@/utils/wallet/types';

interface MoreWalletModalProps {
  trigger: ReactNode;
  children: ReactNode;
  title: string;
}

interface ModalRef {
  close: () => void;
}

const NewTag = () => {
  return <Badge className={'bg-blue-50 hover:bg-blue/80 text-blue-700 rounded-[16px] text-xs font-medium'}>New</Badge>;
};

// eslint-disable-next-line react/display-name
const MoreWalletModal = forwardRef<ModalRef, MoreWalletModalProps>(({ trigger, children, title }, ref) => {
  const [open, setOpen] = useState(false);

  useImperativeHandle(ref, () => {
    return {
      close: () => {
        setOpen(false);
      }
    };
  });

  return (
    <Modal open={open} onOpenChange={setOpen} footer={false} trigger={trigger} className={'md:w-[368px]'}>
      <div className={'text-lg font-medium text-center'}>{title}</div>
      <div>{children}</div>
    </Modal>
  );
});

export default function Login() {
  const [loading, setLoading] = useState(false);
  const searchParams = useSearchParams();
  const [adaWallets, setAdaWallets] = useState<any[]>([]);
  const { toast } = useToast();
  const ref = useRef<ModalRef>(null);

  const signinCallback = async ({
    message,
    key,
    data,
    chainType,
    authType,
    protocolTag,
    address
  }: {
    message: string;
    key: string;
    data: string;
    chainType: string;
    authType: string;
    protocolTag?: string;
    address?: string;
  }) => {
    await login(
      {
        message: message,
        key: key,
        data: data,
        chainType: chainType,
        authType: authType,
        protocolTag: protocolTag,
        address
      },
      (err: any) => {
        const resData = err.response.data;
        if (resData.error === ErrorCode.E001) {
          toast({
            variant: 'error',
            title: 'Setup Your Starknet Wallet',
            description:
              "It appears that your Starknet wallet account hasn't been set up yet. Please set up your account on Starknet and then retry the process."
          });
        } else {
          toast({
            variant: 'error',
            title: err.statusText,
            description: resData.message
          });
        }
      }
    );

    reportGAEvent('pc_account_signin', {
      address: key,
      network: chainType,
      walletName: authType
    });
    const redirect = searchParams.redirect;
    const redirectUrl = redirect ? decodeURIComponent(redirect) : '/dashboard';
    if (/^\/\S*$/.test(redirectUrl)) {
      // 验证输入是否是相对路径
      window.location.href = redirectUrl;
    } else {
      window.location.href = '/';
    }
  };

  useEffect(() => {
    if (isServer()) return;
    const wallets = ADAWallet.getWalletList();
    setAdaWallets(wallets);
  }, []);

  useEffectOnce(() => {
    ParticleWallet.setCallback((data: any) => {
      signWithWallet(WalletType.PARTICLE, data.thirdparty_user_info.provider);
    });

    return () => {
      console.log('Running clean-up of effect on unmount');
    };
  });

  const signWithWallet = async (type: WalletType, accountType?: string) => {
    try {
      const walletType = getUserWalletType(type, accountType);
      const wallet = await WalletFactory.getWallet(walletType);
      console.log(wallet, 'ww');
      // 登录清空userAddress
      wallet.userAddress = undefined;
      const isConnected = await wallet.connect(accountType);
      if (!isConnected) return;
      const { signature, message: fullMessage } = await wallet.signin();

      const data = wallet.getWallet();
      console.log(wallet, signature, fullMessage);
      setLoading(true);
      await signinCallback({
        message: fullMessage,
        key: data.publicKey || data.address,
        data: signature,
        chainType: type,
        authType: wallet.authType,
        protocolTag: wallet.protocolTag,
        address: data.address
      });
    } catch (e: any) {
      console.log(e);
      toast({
        variant: 'error',
        title: 'Sign in failed',
        description: e.message
      });
      setLoading(false);
    }
  };

  const okxWallets = [
    {
      name: 'OKX Wallet',
      icon: <OkxIcon />,
      onClick: () => signWithWallet(WalletType.EVM, WalletType.OKX),
      network: WalletType.EVM
    },
    {
      name: 'OKX Wallet',
      icon: <OkxIcon />,
      onClick: () => signWithWallet(WalletType.BITCOIN, WalletType.OKX),
      network: WalletType.BITCOIN
    }
  ];

  const WALLET_LIST = [
    {
      name: 'Coinbase Wallet',
      icon: <CoinbaseIcon />,
      onClick: () => signWithWallet(WalletType.EVM, WalletType.Coinbase),
      network: WalletType.EVM,
      isNew: true
    },
    {
      name: 'MetaMask',
      icon: <MetamaskIcon />,
      onClick: () => signWithWallet(WalletType.EVM),
      network: WalletType.EVM
    },
    {
      name: 'Wallet Connect',
      icon: <WalletConnectIcon />,
      onClick: () => signWithWallet(WalletType.EVM, WalletType.WALLETCONNECT),
      network: WalletType.EVM
    },
    {
      name: 'OKX Wallet',
      icon: <OkxIcon />,
      render: (
        <MoreWalletModal
          trigger={
            <Button variant="outline" className="border-gray-100 px-[24px] py-[4px] h-12 relative">
              <OkxIcon />
              <span className="ml-[24px] mr-[16px]">OKX Wallet</span>
            </Button>
          }
          title={'Select Network'}
        >
          <div className="flex flex-col gap-[20px] mt-[24px]">
            {okxWallets?.map((item, index) => {
              return (
                <Button
                  variant="outline"
                  className="border-gray-100 px-[24px] py-[4px] h-12 relative"
                  onClick={item.onClick}
                  key={index}
                >
                  {item.icon}
                  <span className="ml-[24px] mr-[16px]">{item.name}</span>
                  <NetworkTag network={item.network} />
                </Button>
              );
            })}
          </div>
        </MoreWalletModal>
      )
    },
    {
      name: 'Ton Connect',
      icon: <TonIcon />,
      onClick: () => signWithWallet(WalletType.TON),
      network: WalletType.TON
    }
  ];

  const SECOND_WALLET_LIST = [
    {
      name: 'Petra Wallet',
      icon: <PetraIcon />,
      onClick: () => signWithWallet(WalletType.APTOS),
      network: WalletType.APTOS
    },
    {
      name: 'Phantom Wallet',
      icon: <PhantomIcon />,
      onClick: () => signWithWallet(WalletType.SOLANA),
      network: WalletType.SOLANA
    },
    {
      name: 'Leather Wallet',
      icon: <LeatherIcon />,
      onClick: () => signWithWallet(WalletType.BITCOIN),
      network: WalletType.BITCOIN
    },
    {
      name: 'StarkNet Wallet',
      icon: <StarkNetIcon />,
      onClick: () => {
        ref.current?.close();
        signWithWallet(WalletType.StarkNet);
      },
      network: WalletType.StarkNet
    },
    {
      name: 'UniSat Wallet',
      icon: <UnisatIcon />,
      onClick: () => signWithWallet(WalletType.BITCOIN, ExtendedWalletType.UNISAT),
      network: WalletType.BITCOIN
    },
    {
      name: 'Cardano Connect',
      icon: <CardanoIcon />,
      network: WalletType.Cardano,
      wrapper: ({ children }: { children: ReactNode }) => {
        return (
          <Dropdown
            items={adaWallets?.map((it) => {
              return {
                label: it.name,
                icon: <img src={it.icon} width={24} height={24} />,
                onClick: () => {
                  signWithWallet(WalletType.Cardano, it.name);
                }
              };
            })}
          >
            {children}
          </Dropdown>
        );
      }
    }
  ];

  return (
    <div className="flex items-center h-screen relative">
      {loading && <Loading />}
      <div className="flex-1 relative h-full">
        <div
          className="absolute top-0 left-0 w-full h-full flex flex-col justify-center items-center bg-cover"
          style={{
            backgroundImage: `url(https://sign-public-cdn.s3.us-east-1.amazonaws.com/signature-next/bg_240723065956.webp)`
          }}
        >
          <div className="relative">
            <div className={'flex justify-end'}>
              <img
                src="https://sign-public-cdn.s3.us-east-1.amazonaws.com/signature-next/loginmodal_240723065953.webp"
                alt={'bg'}
                width={433}
                height={334}
              />
            </div>
            <h1 className="text-[31px] text-center text-white font-bold mt-[69px] mb-[16px]">
              Welcome back! Let&apos;s start signing.
            </h1>
            <div className="flex justify-center">
              <LogoIconLight />
            </div>
          </div>
        </div>
      </div>
      <div className="flex-1 h-full relative flex items-center justify-center bg-white">
        <div className="absolute right-[30px] top-[20px]">
          <LogoIcon />
        </div>
        <div className="w-[350px]">
          <h1 className="font-bold text-xl text-gray-900">Sign in</h1>
          <div className="text-sm text-gray-500 mt-[12px]">Sign in with your wallet or social accounts</div>
          <div className="flex justify-between mt-[32px] gap-5">
            <Button
              variant="outline"
              className="border-gray-100 flex-1 h-12"
              onClick={() => signWithWallet(WalletType.PARTICLE, 'google')}
            >
              <GoogleIcon />
            </Button>
            <Button
              variant="outline"
              className="border-gray-100 flex-1 h-12"
              onClick={() => signWithWallet(WalletType.PARTICLE, 'twitter')}
            >
              <TwitterIcon />
            </Button>
          </div>
          <div className="flex items-center gap-[26px] mt-[24px]">
            <div className="flex-1">
              <Separator />
            </div>
            <div className="text-sm text-gray-400">or continue with</div>
            <div className="flex-1">
              <Separator />
            </div>
          </div>
          <div className="flex flex-col gap-6 mt-6">
            {WALLET_LIST?.map((item, index) => {
              if (item.render) {
                return <Fragment key={index}>{item.render}</Fragment>;
              }
              return (
                <Button
                  variant="outline"
                  className="border-gray-100 px-[24px] py-[4px] h-12 relative"
                  onClick={item.onClick}
                  key={index}
                >
                  {item.icon}
                  <span className="ml-[24px] mr-[16px]">{item.name}</span>
                  {item.isNew ? <NewTag /> : null}
                </Button>
              );
            })}
            <MoreWalletModal
              title={'More Wallets'}
              ref={ref}
              trigger={
                <Button variant="outline" className="border-gray-100 px-[24px] py-[4px] h-12 relative w-full">
                  More Wallets
                </Button>
              }
            >
              <div className="grid grid-cols-3 gap-5 mt-6">
                {SECOND_WALLET_LIST?.map((item) => {
                  if (item.wrapper) {
                    return (
                      <item.wrapper key={item.name}>
                        <Tooltip
                          side="bottom"
                          content={
                            <div className={'flex gap-1 items-center'}>
                              <div className="text-sm text-gray-400">{item.name}</div>
                              <NetworkTag network={item.network} />
                            </div>
                          }
                        >
                          <div
                            className="rounded-md border border-gray-100 flex justify-center items-center py-[10px] h-[48px] relative w-[94px] rounded-[8px]"
                            onClick={item.onClick}
                          >
                            {item.icon}
                          </div>
                        </Tooltip>
                      </item.wrapper>
                    );
                  }
                  return (
                    <Tooltip
                      side="bottom"
                      key={item.name}
                      content={
                        <div className={'flex gap-1 items-center'}>
                          <div className="text-sm text-gray-400">{item.name}</div>
                          <NetworkTag network={item.network} />
                        </div>
                      }
                    >
                      <Button
                        variant="outline"
                        className="border-gray-100 px-0 py-[10px] h-[48px] w-[94px] rounded-[8px] relative"
                        onClick={item.onClick}
                      >
                        {item.icon}
                      </Button>
                    </Tooltip>
                  );
                })}
              </div>
            </MoreWalletModal>
          </div>
        </div>
      </div>
    </div>
  );
}
